import React, {useCallback, useEffect, useState} from 'react';

import {useRemoveSeatFromUser} from 'src/modules/profile/user/hooks/use-remove-seat-from-user';

import Button from '@pb/kit-component-button';
import {CheckBox} from '@pb/kit-component-checkbox';
import {DialogFooter, DialogHeader, DialogMain} from '@pb/kit-component-dialog-v2';
import {Group} from '@pb/kit-component-group';
import {Message} from '@pb/kit-component-message';

import type {UserSeatAssignment} from 'src/openapi/admin/models/UserSeatAssignment';
import type {CheckBoxChangeData} from 'src/typedef';

type Props = {
    userId: number,
    userName: string,
    assignment: UserSeatAssignment,
    onBusyChange: (busy: boolean) => void,
    onClose: () => void,
    onRevoked: () => void,
};

export const RevokeSeatModal: React.FC<Props> = ({
    userId,
    userName,
    assignment,
    onBusyChange,
    onClose,
    onRevoked,
}) => {
    const {remove, removing, error} = useRemoveSeatFromUser(userId);
    const [confirmed, setConfirmed] = useState(false);
    const productName = assignment.product.name;

    useEffect(() => {
        onBusyChange(removing);
    }, [onBusyChange, removing]);

    const onConfirmationChange = useCallback(({event}: CheckBoxChangeData) => {
        setConfirmed(event.currentTarget.checked);
    }, []);
    const onDismiss = useCallback(() => {
        if (!removing) {
            onClose();
        }
    }, [onClose, removing]);
    const onRevoke = useCallback(async () => {
        if (!confirmed || removing) {
            return;
        }

        if (await remove(assignment.productSubscriptionId)) {
            onRevoked();
            onClose();
        }
    }, [assignment.productSubscriptionId, confirmed, onClose, onRevoked, remove, removing]);

    return (
        <>
            <DialogHeader onClose={onDismiss}>Revoke Product Access</DialogHeader>
            <DialogMain style={{padding: 24}}>
                <Group direction="v" gap="l">
                    {error ? <Message color="error">{error}</Message> : null}
                    <p>
                        You are about to revoke <strong>{userName}</strong>&apos;s access to&nbsp;
                        <strong>{productName}</strong>.
                    </p>
                    <Message color="warning">
                        This will immediately remove the user&apos;s seat assignment and they will no longer be able
                        to access this product.
                    </Message>
                    <div style={{border: '1px solid #d9d9d9', borderRadius: 6, padding: 24}}>
                        <CheckBox
                            checked={confirmed}
                            disabled={removing}
                            label={`I confirm that I want to revoke this user's access to ${productName}.`}
                            onCheckBoxChange={onConfirmationChange}
                        />
                    </div>
                </Group>
            </DialogMain>
            <DialogFooter>
                <Group>
                    <Button variant="Type.SECONDARY" busy={removing} disabled={removing} onClick={onDismiss}>Cancel</Button>
                    <Button variant="Type.PRIMARY" busy={removing} disabled={!confirmed || removing} onClick={onRevoke}>
                        Revoke Access
                    </Button>
                </Group>
            </DialogFooter>
        </>
    );
};
